import { getUserData, setUserCrpcen, getAllUserPartenaireId, refreshAllUserPartenaireId, setUserPartenaireId } from "@/taskpane/services/user";
import { MutationOptions, useMutation, useQuery, UseQueryOptions, useQueryClient } from "@tanstack/react-query";

/**
 * Mutation hook to update the user's CRPCEN.
 * 
 * @param {MutationOptions} opt - Optional configuration options for the mutation.
 * @returns {UseMutationResult} A react-query mutation result object.
 */
export const useUpdateCrpcenMutation = (
  opt?: MutationOptions<Awaited<ReturnType<typeof setUserCrpcen>>, Error, Parameters<typeof setUserCrpcen>[0]>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...opt,
    mutationFn: setUserCrpcen,
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries({
        queryKey: ["getUserAttributes"],
      });
      if (opt?.onSuccess) {
        opt.onSuccess(data, variables, context);
      }
    },
  });
};

/**
 * Hook to fetch user data with caching.
 * 
 * The data will be considered fresh for 55 minutes (staleTime),
 * and will be eligible for garbage collection after 1 hour (gcTime) even if unused.
 * This reduces unnecessary API calls while ensuring relatively up-to-date data.
 * 
 * @param {Partial<UseQueryOptions>} opt - Optional configuration options for the query.
 * @returns {UseQueryResult} A react-query result object.
 */
export const useGetUserDataQuery = (
  opt?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserData>>>>
) => {
  const query = useQuery({
    queryKey: ["getUserData"],
    queryFn: getUserData,
    staleTime: 55 * 60 * 1000, // 55 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    ...opt,
  });
  
  // console.log('[useGetUserDataQuery] Query status:', {
  //   isLoading: query.isLoading,
  //   isFetching: query.isFetching,
  //   isError: query.isError,
  //   isSuccess: query.isSuccess,
  //   data: query.data ? 'HAS_DATA' : 'NO_DATA',
  //   enabled: opt?.enabled !== false
  // });
  
  return query;
};

/**
 * Hook to fetch all user partenaire IDs.
 * 
 * The query will only run if:
 * 1. The user hasn't selected their initials yet (no partenaireId)
 * 2. The query is not disabled via options
 * 
 * This prevents unnecessary API calls when the user has already selected their initials.
 * It's useful for scenarios where you need to display the list of all partenaire IDs
 * associated with the current user or client (etude notariale).
 *
 * @param {Partial<UseQueryOptions>} opt - Optional configuration options for the query.
 * @returns {UseQueryResult} A react-query result object.
 */
export const useGetAllUserPartenaireId = (
  opt?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllUserPartenaireId>>>>
) => {
  const queryClient = useQueryClient();
  
  // Get the current user's attributes from the query client cache
  const currentUser = queryClient.getQueryData(["getUserAttributes"]);

  // Query should only be enabled if:
  // 1. User hasn't selected their initials (no partenaireId)
  // 2. The modal is open (opt.enabled is true)
  const shouldEnableQuery = (!currentUser?.partenaireId) && (opt?.enabled !== false);

  // If the user has already selected their initials, disable all automatic refetching
  const finalOptions = {
    ...opt,
    enabled: shouldEnableQuery,
    staleTime: shouldEnableQuery ? (5 * 60 * 1000) : Infinity,
    cacheTime: shouldEnableQuery ? (60 * 60 * 1000) : 0,
    // Disable automatic refetching if the user has already selected their initials
    refetchOnMount: shouldEnableQuery,
    refetchOnReconnect: shouldEnableQuery,
    refetchOnWindowFocus: shouldEnableQuery,
    // Explicitly set refetchInterval to 0 when query should be disabled
    refetchInterval: shouldEnableQuery ? (opt?.refetchInterval ?? 5000) : 0,
    // Prevent retries when disabled
    retry: shouldEnableQuery,
    retryOnMount: shouldEnableQuery,
    // Prevent background updates
    refetchInBackground: shouldEnableQuery,
  };

  const query = useQuery({
    queryFn: () => {
      console.log('[DEBUG] useGetAllUserPartenaireId hook - QUERY EXECUTION:', {
        timestamp: new Date().toISOString(),
        message: 'Query function is being executed',
        currentOptions: {
          enabled: finalOptions.enabled,
          refetchInterval: finalOptions.refetchInterval
        }
      });
      return getAllUserPartenaireId();
    },
    queryKey: ["getAllUserPartenaireId"],
    ...finalOptions,
  });

  return query;
};

/**
 * Mutation hook to refresh the list of all user partenaire IDs.
 * 
 * This is useful when a user can't find their information in the current list
 * and needs to trigger a refresh of the data on the server.
 *
 * @param {MutationOptions} opt - Optional configuration options for the mutation.
 * @returns {UseMutationResult} A react-query mutation result object.
 */
export const useRefreshAllUserPartenaireIdMutation = (
  opt?: MutationOptions<Awaited<ReturnType<typeof refreshAllUserPartenaireId>>, Error, Parameters<typeof refreshAllUserPartenaireId>>
) => useMutation({
  ...opt,
  mutationFn: refreshAllUserPartenaireId,
});

/**
 * Mutation hook to set the user's partenaire ID.
 *
 * Used to associate a specific partenaire ID with the current user.
 * The partenaire ID typically corresponds to the user's initiales in the etude notariale
 * and serves as a unique identifier for the user's external data.
 *
 * @param {MutationOptions} opt - Optional configuration options for the mutation.
 * @returns {UseMutationResult} A react-query mutation result object.
 */
export const useSetUserPartenaireIdMutation = (
  opt?: MutationOptions<Awaited<ReturnType<typeof setUserPartenaireId>>, Error, Parameters<typeof setUserPartenaireId>[0]>
) => useMutation({
  ...opt,
  mutationFn: setUserPartenaireId,
});